import { Plus, Trash2, User } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';

interface Student {
  tempId?: string;
  studentEnrollmentId?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  gradeLevel: string;
  currentSchool: string;
}

interface GradeLevel {
  description: string;
  order: number;
}

interface StudentInfoChunkProps {
  students: Student[];
  onUpdate: (students: Student[]) => void;
  gradeLevels: GradeLevel[];
}

function generateTempId() {
  return `temp-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

function createEmptyStudent(): Student {
  return {
    tempId: generateTempId(),
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    gradeLevel: '',
    currentSchool: '',
  };
}

/**
 * Reusable student info chunk for adding/editing student details.
 * Used by the application form for entering new student information.
 */
export function StudentInfoChunk({ students, onUpdate, gradeLevels }: StudentInfoChunkProps) {
  const addStudent = () => {
    onUpdate([...students, createEmptyStudent()]);
  };

  const removeStudent = (index: number) => {
    onUpdate(students.filter((_, i) => i !== index));
  };

  const updateStudent = (index: number, field: keyof Student, value: string) => {
    const updated = [...students];
    updated[index] = { ...updated[index], [field]: value };
    onUpdate(updated);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {students.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <User className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">
              No students added yet. Add the students you would like to enroll.
            </p>
            <Button onClick={addStudent}>
              <Plus className="h-4 w-4 mr-2" />
              Add Student
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {students.map((student, index) => (
            <Card key={student.studentEnrollmentId || student.tempId}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Student {index + 1}
                  </CardTitle>
                  {students.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeStudent(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <CardDescription>
                  Enter information for this student
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`student-first-${index}`}>
                      First Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`student-first-${index}`}
                      value={student.firstName || ''}
                      onChange={(e) => updateStudent(index, 'firstName', e.target.value)}
                      placeholder="First name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`student-last-${index}`}>
                      Last Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`student-last-${index}`}
                      value={student.lastName || ''}
                      onChange={(e) => updateStudent(index, 'lastName', e.target.value)}
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`student-dob-${index}`}>
                      Date of Birth <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`student-dob-${index}`}
                      type="date"
                      value={student.dateOfBirth || ''}
                      onChange={(e) => updateStudent(index, 'dateOfBirth', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`student-gender-${index}`}>Gender</Label>
                    <Select
                      value={student.gender || ''}
                      onValueChange={(val) => updateStudent(index, 'gender', val)}
                    >
                      <SelectTrigger id={`student-gender-${index}`}>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MALE">Male</SelectItem>
                        <SelectItem value="FEMALE">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`student-grade-${index}`}>
                      Grade Level <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={student.gradeLevel || ''}
                      onValueChange={(val) => updateStudent(index, 'gradeLevel', val)}
                    >
                      <SelectTrigger id={`student-grade-${index}`}>
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {gradeLevels.map((grade) => (
                          <SelectItem key={grade.description} value={grade.description}>
                            {grade.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`student-school-${index}`}>Current School</Label>
                    <Input
                      id={`student-school-${index}`}
                      value={student.currentSchool || ''}
                      onChange={(e) => updateStudent(index, 'currentSchool', e.target.value)}
                      placeholder="Current school name"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <Button variant="outline" onClick={addStudent} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Another Student
          </Button>
        </>
      )}
    </div>
  );
}
